import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X, Expand } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { SEED_GALLERY } from "../lib/siteData";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [hover, setHover] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api
      .get("/site/gallery")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) setItems(r.data);
        else setItems(SEED_GALLERY);
      })
      .catch(() => setItems(SEED_GALLERY));
  }, []);

  const step = useCallback(
    (dir) => setActive((i) => (i === null ? i : (i + dir + items.length) % items.length)),
    [items.length]
  );

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, step]);

  const current = active === null ? null : items[active];

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-20 sm:py-28 bg-ivorySoft border-y border-ink-10"
      style={{ borderColor: "rgba(11,31,56,0.12)" }}
    >
      <div className="container-prose">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <p className="overline">10 — Gallery</p>
            <h2 className="h2-display mt-4 text-4xl sm:text-5xl text-ink">
              Moments from the <span className="italic text-forest">field</span>.
            </h2>
          </div>
          <p className="body-prose text-ink/70 max-w-md">
            Workshops, keynotes, classrooms — a working life in pictures. Click any frame to enlarge.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5">
          {items.map((g, i) => (
            <motion.figure
              key={g.id || i}
              data-testid={`gallery-item-${i}`}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              onClick={() => setActive(i)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative overflow-hidden bg-ink/5 cursor-pointer transition-opacity duration-500 ${
                hover !== null && hover !== i ? "opacity-50" : "opacity-100"
              } ${i % 5 === 0 ? "md:row-span-2 md:aspect-[3/4]" : "aspect-square"}`}
            >
              <img
                src={mediaUrl(g.image_url)}
                alt={g.caption || "Gallery"}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <span className="absolute top-3 right-3 h-9 w-9 grid place-items-center bg-ivory/90 text-ink opacity-0 group-hover:opacity-100 transition-opacity">
                <Expand size={14} />
              </span>
              {g.caption && (
                <figcaption className="absolute bottom-0 inset-x-0 px-4 py-3 bg-gradient-to-t from-ink/85 to-transparent text-ivory text-[13px] tracking-wide opacity-0 group-hover:opacity-100 transition-opacity">
                  {g.caption}
                </figcaption>
              )}
            </motion.figure>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {current && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-md flex flex-col items-center justify-center px-4 py-16"
          >
            <button
              data-testid="lightbox-close"
              onClick={() => setActive(null)}
              className="absolute top-5 right-5 h-11 w-11 grid place-items-center border border-ivory/25 text-ivory hover:bg-ivory hover:text-ink transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              className="absolute left-3 sm:left-8 h-12 w-12 grid place-items-center border border-ivory/25 text-ivory hover:bg-ivory hover:text-ink transition-colors"
              aria-label="Previous"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              className="absolute right-3 sm:right-8 h-12 w-12 grid place-items-center border border-ivory/25 text-ivory hover:bg-ivory hover:text-ink transition-colors"
              aria-label="Next"
            >
              <ChevronRight size={20} />
            </button>

            <motion.img
              key={active}
              data-testid="lightbox-image"
              src={mediaUrl(current.image_url)}
              alt={current.caption || "Gallery"}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-[74vh] max-w-[88vw] object-contain shadow-2xl"
            />
            <div className="mt-6 text-center max-w-2xl" onClick={(e) => e.stopPropagation()}>
              {current.caption && (
                <p
                  data-testid="lightbox-caption"
                  className="text-ivory text-lg italic"
                  style={{ fontFamily: '"Cormorant Garamond", serif' }}
                >
                  {current.caption}
                </p>
              )}
              <p className="mt-3 text-[11px] uppercase tracking-[0.28em] text-ivory/50">
                {active + 1} / {items.length}
                {current.location ? ` · ${current.location}` : ""}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
